import React, { useState, useEffect } from 'react';
import { Table, ColumnDefinition } from '@/UIlibrary/Table/Table';
import { GRAY_COLORS, BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { ChevronDown, ChevronUp, AlertCircle, Search, X, Calendar } from 'react-feather';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	useRegistriesQuery,
	Registry,
	SortDirection,
	RegistrySort,
	RegistryFilter,
	OnboardingStatus
} from '@/graphql/__generated__/onboarding-legal';
import Button from '@/UIlibrary/Button/Button';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import InputField from '@/UIlibrary/InputField/InputField';
import MultiSelectOnboardingStatus from '@/pages/Onboarding/MultiSelectOnboardingStatus';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { es } from 'date-fns/locale';
import { formatDateTime, getLanguageFromPath } from '@/utils/utils';
import { OnboardingStatusBadge } from '@/pages/Onboarding/OnboardingStatusBadge';
import StepIndicator from '@/stories/Components/StepIndicator/StepIndicator';

const PAGE_SIZE = 10;

type RegistryTableData = Registry[];

const RegistryTable: React.FC = () => {
	const t = useTranslations('Registries');
	const [searchParams] = useSearchParams();
	const clientId = searchParams.get('clientId');
	const language = getLanguageFromPath(window.location.pathname);

	const navigate = useNavigate();
	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING_LEGAL);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
	const [sortField, setSortField] = useState<'onboardingStatus' | 'startedAt'>('startedAt');
	const [searchInput, setSearchInput] = useState<string>(clientId ?? '');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
	const [selectedStatuses, setSelectedStatuses] = useState<OnboardingStatus[]>([]);
	const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined;
		endDate: Date | undefined;
		key: string;
	}>({
		startDate: undefined,
		endDate: undefined,
		key: 'selection'
	});

	useEffect(() => {
		const timerId = setTimeout(() => {
			setDebouncedSearchTerm(searchInput);
		}, 300);

		return () => clearTimeout(timerId);
	}, [searchInput]);

	const getFilter = (): RegistryFilter | undefined => {
		const filter: RegistryFilter = {};

		if (debouncedSearchTerm) {
			filter.searchTerm = debouncedSearchTerm;
		}

		if (selectedStatuses.length > 0) {
			filter.onboardingStatus = selectedStatuses;
		}

		if (dateRange.startDate || dateRange.endDate) {
			filter.startedAt = {};

			if (dateRange.startDate) {
				// Set start date to beginning of the day (00:00:00)
				const startDate = new Date(dateRange.startDate);
				startDate.setHours(0, 0, 0, 0);
				filter.startedAt.from = startDate.toISOString();
			}

			if (dateRange.endDate) {
				// Set end date to end of the day (23:59:59.999)
				const endDate = new Date(dateRange.endDate);
				endDate.setHours(23, 59, 59, 999);
				filter.startedAt.to = endDate.toISOString();
			}
		}

		// Return undefined if no filters applied
		return Object.keys(filter).length ? filter : undefined;
	};

	const getCurrentSort = (): RegistrySort => ({
		[sortField]: sortDirection
	});

	const { loading, error, data, fetchMore, refetch } = useRegistriesQuery({
		client: onboardingClient,
		variables: {
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		},
		notifyOnNetworkStatusChange: true
	});

	// Effect to refetch when filters change
	useEffect(() => {
		refetch({
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		});
	}, [debouncedSearchTerm, sortDirection, sortField, selectedStatuses, dateRange, refetch]);

	const handleLoadMore = async () => {
		if (!data?.registries?.pageInfo.hasNextPage) return;

		const lastCursor = data.registries?.edges?.[data.registries.edges.length - 1]?.cursor;
		if (!lastCursor) return;

		try {
			await fetchMore({
				variables: {
					after: lastCursor,
					first: PAGE_SIZE,
					sorts: [getCurrentSort()],
					filter: getFilter()
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.registries) return prev;

					return {
						registries: {
							...fetchMoreResult.registries,
							edges: [
								...(prev.registries?.edges || []),
								...(fetchMoreResult.registries.edges || [])
							]
						}
					};
				}
			});
		} catch (error) {
			console.error('Error loading more registries:', error);
		}
	};

	const handleSortChange = (field: 'onboardingStatus' | 'startedAt') => {
		if (field === sortField) {
			// If clicking on the same field, toggle sort direction
			setSortDirection(
				sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
			);
		} else {
			// If clicking on a different field, set it as the new sort field and default to DESC
			setSortField(field);
			setSortDirection(SortDirection.DESC);
		}
	};

	const handleClearSearch = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
	};

	const handleStatusFilterChange = (statuses: OnboardingStatus[]) => {
		setSelectedStatuses(statuses);
	};

	const getOnboardingStatusText = (status: OnboardingStatus): string => {
		const statusMap: Record<OnboardingStatus, string> = {
			[OnboardingStatus.IN_PROCESS]: t('InProcess'),
			[OnboardingStatus.CORRECTIONS_REQUESTED]: t('CorrectionsRequested'),
			[OnboardingStatus.PENDING_REVIEW]: t('PendingReview'),
			[OnboardingStatus.REJECTED]: t('Rejected'),
			[OnboardingStatus.APPROVED]: t('Approved')
		};

		return statusMap[status] || t('InProcess');
	};

	const getFullName = (registry: Registry): string => {
		const { legalRepresentative } = registry;
		if (!legalRepresentative) return '-';

		const { names, firstLastName, secondLastName } = legalRepresentative;
		return [names, firstLastName, secondLastName].filter(Boolean).join(' ').trim() || '-';
	};

	const SortIcon = ({ field }: { field: 'onboardingStatus' | 'startedAt' }) => {
		if (field !== sortField) return null;

		const Icon = sortDirection === SortDirection.ASC ? ChevronUp : ChevronDown;
		return <Icon size={16} className="text-gray-500" />;
	};

	const handleViewDetails = (registryId: string) => {
		navigate(`/${language}/center/clients/legal/${registryId}`);
	};

	const renderViewDetailsButton = (registry: Registry) => (
		<Button textOnly onClick={() => handleViewDetails(registry.id)} label={t('ViewDetails')} />
	);

	const renderLegalRepresentativeName = (registry: Registry) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{getFullName(registry)}
		</Typography>
	);

	const renderRegistryStatus = (registry: Registry) => (
		<div className="flex gap-2">
			<OnboardingStatusBadge status={registry.onboardingStatus as OnboardingStatus} size="sm" />
		</div>
	);

	const renderEmail = (registry: Registry) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{registry.email || '-'}
		</Typography>
	);

	const renderPhoneNumber = (registry: Registry) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{registry.phoneNumber || '-'}
		</Typography>
	);

	const renderStartedAt = (registry: Registry) => {
		if (!registry.startedAt)
			return (
				<Typography size="sm" color={GRAY_COLORS.gray700}>
					-
				</Typography>
			);

		return (
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{formatDateTime(registry.startedAt)}
			</Typography>
		);
	};

	const columns: ColumnDefinition[] = [
		{
			header: '',
			key: 'actions',
			cellRenderer: (_, registry: Registry) => renderViewDetailsButton(registry)
		},
		{
			header: t('CompanyName'),
			key: 'companyInformation',
			cellRenderer: (_, registry: Registry) => <span>{registry.companyInformation?.legalName}</span>
		},
		{
			header: t('LegalRepresentative'),
			key: 'legalRepresentative',
			cellRenderer: (_, registry: Registry) => renderLegalRepresentativeName(registry)
		},
		{
			header: t('RegisteredEmail'),
			key: 'email',
			cellRenderer: (_, registry: Registry) => renderEmail(registry)
		},
		{
			header: t('RegisteredPhoneNumber'),
			key: 'phoneNumber',
			cellRenderer: (_, registry: Registry) => renderPhoneNumber(registry)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer"
					onClick={() => handleSortChange('startedAt')}
					role="button"
					tabIndex={0}
				>
					<span>{t('StartedAt')}</span>
					<SortIcon field="startedAt" />
				</div>
			),
			key: 'startedAt',
			cellRenderer: (_, registry: Registry) => renderStartedAt(registry)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer"
					onClick={() => handleSortChange('onboardingStatus')}
					role="button"
					tabIndex={0}
				>
					<span>{t('Status')}</span>
					<SortIcon field="onboardingStatus" />
				</div>
			),
			key: 'onboardingStatus',
			cellRenderer: (_, registry: Registry) => renderRegistryStatus(registry)
		}
	];

	const registrySteps = [
		t('LegalContactInformationStepStatus'),
		t('CompanyInformationStepStatus'),
		t('CompanyDocumentsStepStatus'),
		t('LegalRepresentativeStepStatus'),
		t('PendingReview'),
		t('CorrectionsRequested'),
		t('ApprovedRejected')
	];

	// Status filter options
	const statusOptions = [
		{
			value: OnboardingStatus.IN_PROCESS,
			label: getOnboardingStatusText(OnboardingStatus.IN_PROCESS)
		},
		{
			value: OnboardingStatus.CORRECTIONS_REQUESTED,
			label: getOnboardingStatusText(OnboardingStatus.CORRECTIONS_REQUESTED)
		},
		{
			value: OnboardingStatus.PENDING_REVIEW,
			label: getOnboardingStatusText(OnboardingStatus.PENDING_REVIEW)
		},
		{ value: OnboardingStatus.REJECTED, label: getOnboardingStatusText(OnboardingStatus.REJECTED) },
		{ value: OnboardingStatus.APPROVED, label: getOnboardingStatusText(OnboardingStatus.APPROVED) }
	];

	if (error) {
		return (
			<div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-md">
				<AlertCircle size={16} />
				<Typography size="sm">{t('ErrorLoadingRegistries')}</Typography>
			</div>
		);
	}

	const isInitialLoading = loading && !data;
	const registries: RegistryTableData =
		(data?.registries?.edges?.map((edge) => edge?.node).filter(Boolean) as Registry[]) || [];
	const totalCount = data?.registries?.count || 0;
	const hasMore = data?.registries?.pageInfo.hasNextPage || false;

	const handleClearAllFilters = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
		setSelectedStatuses([]);
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	const handleDateRangeChange = (
		rangesByKey: Record<string, { startDate?: Date; endDate?: Date }>
	) => {
		const selection = rangesByKey.selection;
		if (!selection?.startDate || !selection?.endDate) return;
		const { startDate, endDate } = selection;
		setDateRange({
			startDate,
			endDate,
			key: 'selection'
		});
		setShowDatePicker(false);
	};

	const handleClearDateRange = () => {
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	// Define search action buttons based on whether search is active
	const searchActions = searchInput ? (
		<div className="flex items-center">
			<Button
				label={t('ClearSearch')}
				endIcon={<X size={18} />}
				onClick={handleClearSearch}
				textOnly
				size="auto"
				data-testid="clear-search-button"
			/>
		</div>
	) : (
		<Search size={18} className="text-gray-500" />
	);

	return (
		<div className="flex flex-col py-4 gap-4">
			<div className="mb-8">
				<StepIndicator currentStep={-1} steps={registrySteps} />
			</div>
			{/* Search and filter section */}
			<div className="flex flex-wrap gap-2 mb-4 items-center">
				<div className="flex-1">
					<InputField
						onChange={(e) => setSearchInput(e.target.value)}
						value={searchInput}
						shape="pill"
						placeholder={t('SearchLegal')}
						dataTestId="searchInput"
						endAction={searchActions}
					/>
				</div>

				<MultiSelectOnboardingStatus
					selectedStatuses={selectedStatuses}
					onChange={handleStatusFilterChange}
					statusOptions={statusOptions}
					placeholder={t('SelectAnOption')}
					label={t('SelectStatuses')}
				/>

				{(searchInput || selectedStatuses.length > 0 || dateRange.startDate) && (
					<Button
						onClick={handleClearAllFilters}
						size="auto"
						textOnly
						data-testid="clear-filters-button"
						label={t('ClearFilters')}
					/>
				)}
			</div>

			{/* Date picker section */}
			<div className="relative mb-4">
				<Button
					onClick={() => setShowDatePicker(!showDatePicker)}
					size="auto"
					endIcon={<Calendar size={20} />}
					label={
						dateRange.startDate && dateRange.endDate
							? `${formatDateTime(dateRange.startDate.toISOString()).split(' ')[0]} - ${formatDateTime(dateRange.endDate.toISOString()).split(' ')[0]}`
							: t('SelectDateRange')
					}
				/>

				<span className="ml-5">
					{dateRange.startDate && (
						<Button onClick={handleClearDateRange} size="auto" textOnly label={t('ClearDates')} />
					)}
				</span>

				{showDatePicker && (
					<div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg">
						<DateRange
							showMonthAndYearPickers={true}
							showDateDisplay={false}
							ranges={[dateRange]}
							months={1}
							onChange={handleDateRangeChange}
							locale={es}
							moveRangeOnFirstSelection={false}
							direction="horizontal"
							rangeColors={[BRAND_COLORS.brand500]}
							minDate={new Date(2020, 0, 1)}
							maxDate={new Date()}
						/>
					</div>
				)}
			</div>

			<div className="custom-scrollbar max-h-[50vh] overflow-y-auto">
				{isInitialLoading ? (
					<div className="flex justify-center items-center h-64">
						<div className="loader" />
					</div>
				) : (
					<>
						<Table dataTestId="RegistryTable" columns={columns} data={registries} />

						{hasMore && (
							<div className="text-center py-4">
								<Button
									onClick={handleLoadMore}
									size="auto"
									label={t('LoadMore')}
									disabled={loading}
								/>
							</div>
						)}

						{!loading && registries.length === 0 && (
							<div className="text-center py-4 text-gray-500">{t('NoResults')}</div>
						)}
					</>
				)}
			</div>

			{loading && !isInitialLoading && (
				<div className="flex justify-center py-4">
					<div className="loader" />
				</div>
			)}

			{!isInitialLoading && totalCount > 0 && (
				<div className="mt-1 ml-5 border-t border-b py-3">
					<Typography size="sm" color={GRAY_COLORS.gray600}>
						{t('ShowingResults')}{' '}
						<span className="font-medium">
							{registries.length.toLocaleString()} {t('Of')} {totalCount.toLocaleString()}
						</span>
					</Typography>
				</div>
			)}
		</div>
	);
};

export default RegistryTable;
